import React, { useState, useEffect } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  loading?: 'lazy' | 'eager';
  onClick?: () => void;
}

/**
 * Componente para optimizar la carga de imágenes
 * - Carga perezosa (lazy loading)
 * - Placeholder mientras carga
 * - Manejo de errores
 * - Atributos para SEO
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  className = '',
  loading = 'lazy',
  onClick
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  // Reiniciar estados cuando cambia la fuente de la imagen
  useEffect(() => {
    setIsLoaded(false);
    setError(false);
  }, [src]);
  
  // Determinar si es una imagen de Cloudinary
  const isCloudinaryImage = src && typeof src === 'string' && src.includes('cloudinary.com');
  
  // Verificar si la URL es válida
  const isValidUrl = src && typeof src === 'string' && (src.startsWith('http') || src.startsWith('/'));
  
  // Optimizar URL de Cloudinary si es necesario
  let optimizedSrc = src;
  if (isValidUrl) {
    if (isCloudinaryImage && src.includes('/upload/')) {
      optimizedSrc = src.replace('/upload/', '/upload/q_auto,f_auto,w_auto,dpr_auto/');
    }
  } else {
    // Si la URL no es válida, usar la imagen predeterminada
    optimizedSrc = '/default-avatar.png';
  }
  
  // Manejar carga de imagen
  const handleLoad = () => {
    setIsLoaded(true);
  };
  
  // Manejar error de carga
  const handleError = () => {
    setError(true);
    console.error(`Error al cargar la imagen: ${src}`);
    // Si hay error al cargar la imagen y no es la imagen predeterminada
    // No hacemos nada más aquí para evitar recursión infinita
  };
  
  return (
    <div className={`relative overflow-hidden ${className}`} style={{ width, height }}>
      {/* Placeholder mientras carga */}
      {!isLoaded && !error && (
        <div 
          className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse"
          style={{ width, height }}
        />
      )}
      
      {/* Imagen con error */}
      {error && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500"
          style={{ width, height }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      
      {/* Imagen real */}
      <img
        src={optimizedSrc}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
        onClick={onClick}
        className={`${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300 ${error ? 'hidden' : ''}`}
        style={{ objectFit: 'cover' }}
        // Atributos para SEO
        decoding="async"
        fetchPriority={loading === 'eager' ? 'high' : 'auto'}
      />
    </div>
  );
};

export default OptimizedImage;
